import { InputHTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import { FormInput } from '@/components/Input';

import { CompanyInfoForm } from './schema';

const companyInfoFieldNames = [
  'company_name',
  'company_website',
  'city',
  'country',
  'state',
  'post_code',
  'street_address',
  'company_phone_number',
] as const;

type CompanyInfoField = {
  name: (typeof companyInfoFieldNames)[number];
  label: string;
} & InputHTMLAttributes<HTMLInputElement>;

export function CompanyInformation({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext<CompanyInfoForm>();
  const location = useLocation();

  const isBulkEdit = location.pathname === '/edit-shared-profile';

  const address: CompanyInfoField[] = [
    {
      name: 'city',
      label: 'City',
      placeholder: isBulkEdit ? 'Click to clear or edit' : undefined,
    },
    {
      name: 'state',
      label: 'State / province',
      placeholder: isBulkEdit ? 'Click to clear or edit' : undefined,
    },
    {
      name: 'post_code',
      label: 'Postcode',
      autoComplete: 'postal-code',
      placeholder: isBulkEdit ? 'Click to clear or edit' : undefined,
    },
  ];

  const companyInfoFields: Record<string, CompanyInfoField[]> = {
    first: [
      {
        name: 'company_name',
        label: 'Company name',
        autoComplete: 'organization',
        placeholder: isBulkEdit ? 'Click to clear or edit' : undefined,
      },
      {
        name: 'company_website',
        label: 'Company website',
        autoComplete: 'url',
        placeholder: isBulkEdit ? 'Click to clear or edit' : undefined,
      },
    ],
  };

  return (
    <InfoPanelContainer>
      <div className="text-base text-gray-900 font-medium capitalize pb-6 border-b border-gray-200">
        Company information
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-6">
        {/* Company name and website */}
        <div className="grid grid-cols-1 xl:grid-cols-2 xl:col-span-2 gap-4">
          {companyInfoFields.first.map(field => (
            <div key={field.name} className="flex flex-col space-y-2">
              <FormInput
                error={!!errors[field.name]}
                disabled={disabled}
                {...field}
                {...register(field.name)}
              />
              {errors[field.name] && (
                <span className="text-sm text-red-400 font-medium">
                  {errors[field.name]?.message}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Company phone number */}
        <FormInput
          label="Company phone number"
          placeholder={
            isBulkEdit ? 'Click to clear or edit' : 'Enter company number'
          }
          error={!!errors.company_phone_number}
          disabled={disabled}
          {...register('company_phone_number')}
        />

        {/* Street address */}
        <div className="xl:col-span-2">
          <FormInput
            error={!!errors.street_address}
            label="Street address"
            autoComplete="street-address"
            placeholder={isBulkEdit ? 'Click to clear or edit' : undefined}
            disabled={disabled}
            {...register('street_address')}
          />
        </div>

        {/* City, state, and post code */}
        <div className="grid grid-cols-1 xl:grid-cols-3 xl:col-span-2 gap-4">
          {address.map((item, index) => (
            <FormInput
              key={index}
              error={!!errors[item.name]}
              disabled={disabled}
              {...item}
              {...register(item.name)}
            />
          ))}
        </div>

        {/* Country */}
        <div className="col-span-1">
          <FormInput
            label="Country"
            autoComplete="country-name"
            error={!!errors.country}
            placeholder={isBulkEdit ? 'Click to clear or edit' : undefined}
            disabled={disabled}
            {...register('country')}
          />
        </div>
      </div>
    </InfoPanelContainer>
  );
}
